/* ============================================================
   tz-cart.js — Cart-Drawer AJAX interactions.
   Loaded globally (cart-drawer can be opened from any page).

   Responsibilities:
     1. Qty stepper +/− → /cart/change.js by line key
     2. Qty input direct edit (debounced) → /cart/change.js
     3. Remove button → /cart/change.js with qty=0
     4. Upsell "+ Add" forms → /cart/add.js, then re-render drawer
     5. Gift-note / order-note save (debounced) → /cart/update.js
     6. Cart-count badge global sync (header + bottombar)
     7. After every mutation: refetch drawer section HTML and swap
        the [data-cart-drawer-inner] inner contents

   Co-operates with tz-shop.js (drawer open/close) + tz-pdp.js
   (which already dispatches "tz:open-drawer" on ATC). On every
   /cart/* mutation we dispatch "tz:cart-updated" so tz-shop.js
   can update [data-tz-cart-count] badges.
   ============================================================ */

(function () {
  'use strict';

  var SECTION_ID = 'tz-cart-drawer';
  var inflight = 0;

  /* ──────────────────────────────────────────────────────
     Helpers
     ────────────────────────────────────────────────────── */
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function setBusy(on) {
    var drawer = document.querySelector('[data-tz-drawer="cart"]');
    if (!drawer) return;
    inflight += on ? 1 : -1;
    inflight = Math.max(0, inflight);
    drawer.dataset.busy = inflight > 0 ? 'true' : 'false';
    if (inflight > 0) drawer.style.pointerEvents = 'none';
    else drawer.style.pointerEvents = '';
  }

  /* ──────────────────────────────────────────────────────
     Section render — swap drawer inner from server HTML
     ────────────────────────────────────────────────────── */
  function refreshDrawer() {
    var url = '/?section_id=' + SECTION_ID;
    return fetch(url, { headers: { 'Accept': 'text/html', 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newInner = doc.querySelector('[data-cart-drawer-inner]');
        var currentInner = document.querySelector('[data-cart-drawer-inner]');
        if (newInner && currentInner) {
          currentInner.innerHTML = newInner.innerHTML;
          bindDrawerControls();
        }
      });
  }

  function refreshCartState() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Update all cart-count badges
        document.querySelectorAll('[data-tz-cart-count], [data-cart-count]').forEach(function (el) {
          var c = cart.item_count || 0;
          el.textContent = c;
          if (el.hasAttribute('hidden') || el.style.display !== '') {
            if (c === 0) el.hidden = true;
            else el.hidden = false;
          }
        });
        document.dispatchEvent(new CustomEvent('tz:cart-updated', { detail: cart }));
        return cart;
      });
  }

  /* ──────────────────────────────────────────────────────
     Cart mutations
     ────────────────────────────────────────────────────── */
  function cartChange(key, qty) {
    setBusy(true);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: key, quantity: qty })
    })
      .then(function (r) { return r.json(); })
      .then(function () { return refreshDrawer(); })
      .then(function () { return refreshCartState(); })
      .catch(function (err) {
        console.warn('[tz-cart] change failed', err);
        return refreshDrawer();
      })
      .finally(function () { setBusy(false); });
  }

  function cartUpdate(updates) {
    // updates: { lineKey: qty, … } OR { note: "..." }
    setBusy(true);
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(updates)
    })
      .then(function (r) { return r.json(); })
      .then(function () { return refreshDrawer(); })
      .then(function () { return refreshCartState(); })
      .finally(function () { setBusy(false); });
  }

  function cartAddVariant(variantId, props) {
    setBusy(true);
    var body = { id: variantId, quantity: 1 };
    if (props) body.properties = props;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .then(function () { return refreshDrawer(); })
      .then(function () { return refreshCartState(); })
      .then(function () {
        document.dispatchEvent(new CustomEvent('tz:open-drawer', { detail: 'cart' }));
      })
      .finally(function () { setBusy(false); });
  }

  /* ──────────────────────────────────────────────────────
     Bind controls (called on init + after every refresh)
     ────────────────────────────────────────────────────── */
  function bindDrawerControls() {
    var drawer = document.querySelector('[data-tz-drawer="cart"]');
    if (!drawer) return;

    // Qty +/− buttons
    drawer.querySelectorAll('[data-tz-qty-inc], [data-tz-qty-dec]').forEach(function (btn) {
      if (btn.dataset.tzBound === '1') return;
      btn.dataset.tzBound = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var key = btn.getAttribute('data-cart-update');
        var line = btn.closest('[data-cart-line]');
        var input = line && line.querySelector('[data-tz-qty]');
        if (!input || !key) return;
        var v = parseInt(input.value || '1', 10);
        v = btn.hasAttribute('data-tz-qty-inc') ? v + 1 : Math.max(0, v - 1);
        input.value = v;
        cartChange(key, v);
      });
    });

    // Qty input direct edit (debounced)
    drawer.querySelectorAll('[data-tz-qty]').forEach(function (input) {
      if (input.dataset.tzBound === '1') return;
      input.dataset.tzBound = '1';
      var apply = debounce(function () {
        var key = input.getAttribute('data-cart-update');
        var v = Math.max(0, parseInt(input.value || '0', 10) || 0);
        if (key) cartChange(key, v);
      }, 500);
      input.addEventListener('input', apply);
      input.addEventListener('change', apply);
    });

    // Remove buttons
    drawer.querySelectorAll('[data-cart-remove]').forEach(function (btn) {
      if (btn.dataset.tzBound === '1') return;
      btn.dataset.tzBound = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var key = btn.getAttribute('data-cart-remove');
        var line = btn.closest('[data-cart-line]');
        // Optimistic: fade the line, then remove via cartChange(key, 0)
        if (line) line.style.opacity = '0.4';
        if (key) cartChange(key, 0);
      });
    });

    // Upsell add form
    drawer.querySelectorAll('.tz-drw-upsell form, .tz-cart__pair form').forEach(function (form) {
      if (form.dataset.tzBound === '1') return;
      form.dataset.tzBound = '1';
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var idInput = form.querySelector('input[name="id"]');
        if (!idInput) return;
        var btn = form.querySelector('button[type="submit"]');
        if (btn) {
          var orig = btn.textContent;
          btn.textContent = '✓';
          setTimeout(function () { if (btn.textContent === '✓') btn.textContent = orig; }, 1400);
        }
        cartAddVariant(idInput.value);
      });
    });

    // Order-note save (debounced)
    var noteEl = drawer.querySelector('textarea[name="note"]');
    if (noteEl && noteEl.dataset.tzBound !== '1') {
      noteEl.dataset.tzBound = '1';
      var saveNote = debounce(function () {
        cartUpdate({ note: noteEl.value });
      }, 600);
      noteEl.addEventListener('input', saveNote);
    }

    // Gift-note save → attaches as cart attribute on all items (broad-stroke approach)
    var giftEl = drawer.querySelector('textarea[name="properties[Geschenk-Notiz]"]');
    if (giftEl && giftEl.dataset.tzBound !== '1') {
      giftEl.dataset.tzBound = '1';
      var saveGift = debounce(function () {
        cartUpdate({ attributes: { 'Geschenk-Notiz': giftEl.value } });
      }, 600);
      giftEl.addEventListener('input', saveGift);
    }
  }

  /* ──────────────────────────────────────────────────────
     Listen for tz:add-to-cart custom event from PDP
     ────────────────────────────────────────────────────── */
  document.addEventListener('tz:cart-add-variant', function (e) {
    var detail = e.detail || {};
    if (!detail.id) return;
    cartAddVariant(detail.id, detail.properties || null);
  });

  /* ──────────────────────────────────────────────────────
     Refresh cart drawer when drawer opens (in case something
     changed via another tab / external mutation)
     ────────────────────────────────────────────────────── */
  document.addEventListener('tz:open-drawer', function (e) {
    if (e.detail === 'cart') {
      // Best-effort silent refresh
      refreshDrawer().catch(function () {});
    }
  });

  /* ──────────────────────────────────────────────────────
     Init
     ────────────────────────────────────────────────────── */
  function init() {
    bindDrawerControls();
    // Sync count once on load (cookies may have been updated server-side)
    refreshCartState().catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Export for debugging / programmatic access
  window.TZCart = {
    change: cartChange,
    update: cartUpdate,
    addVariant: cartAddVariant,
    refreshDrawer: refreshDrawer,
    refreshCartState: refreshCartState
  };
})();
